import { hintClass } from "../../requests/components/parts";
import { FieldGroup, TextAreaField } from "../../ui/fields";
import { RadioField } from "../../ui/radio";
import type { ItineraryCommand, ItineraryCommandResult } from "../commands";
import type { Operation } from "../operations";
import type { Detail } from "../view-model";

// What every edit dialog shares: how the operation list is sent, and the "how to save" and "reason" controls.
//
// The version tokens come from the plan the page read, never from a guess:
//   a DRAFT is edited in place with `expectedVersion`   (`editItineraryDraft`, MD-I10);
//   the ACTIVE plan is edited with `baseItineraryId` and `expectedRevision`, as `APPLY` (an atomic new revision) or `PROPOSE`
//   (a staff-only draft revision of the plan)           (`applyItineraryEdit` / `proposeItineraryEdit`, MD-I11).

export type PlanContext = Readonly<{
  /** The plan as it was when the dialog opened. Its version tokens, and only they, go into the command. */
  detail: Detail;
  /** The plan the page reads now differs from `detail`: the dialog must not send. */
  stale: boolean;
  tripId: string;
  run: (request: ItineraryCommand) => Promise<ItineraryCommandResult>;
}>;

export type SaveMode = "APPLY" | "PROPOSE";
export type SaveState = { mode: SaveMode; reason: string };

export const MAX_REASON = 500;

export const initialSave: SaveState = { mode: "APPLY", reason: "" };

export function sendOperations(
  context: PlanContext,
  operations: readonly Operation[],
  save: SaveState,
): Promise<ItineraryCommandResult> {
  const { detail, tripId, run } = context;
  const reason = save.reason.trim();
  const reasonField = reason === "" ? {} : { reason };
  if (detail.itinerary.status === "DRAFT") {
    return run({
      command: "editDraft",
      itineraryId: detail.itinerary.id,
      body: {
        expectedVersion: detail.itinerary.version,
        operations: [...operations],
        ...reasonField,
      },
    });
  }
  return run({
    command: "edit",
    tripId,
    body: {
      mode: save.mode,
      baseItineraryId: detail.itinerary.id,
      expectedRevision: detail.itinerary.revision,
      operations: [...operations],
      ...reasonField,
    },
  });
}

export function isEditable(detail: Detail): boolean {
  return detail.itinerary.status === "DRAFT" || detail.itinerary.status === "ACTIVE";
}

type Props = Readonly<{
  status: Detail["itinerary"]["status"];
  value: SaveState;
  onChange: (next: SaveState) => void;
  reasonRequired: boolean;
  reasonError?: string | undefined;
}>;

export function SaveSettings({ status, value, onChange, reasonRequired, reasonError }: Props) {
  return (
    <>
      {status === "ACTIVE" ? (
        <FieldGroup legend="How to save">
          <RadioField
            id="save-mode-apply"
            name="save-mode"
            checked={value.mode === "APPLY"}
            onChange={() => onChange({ ...value, mode: "APPLY" })}
            label="Apply to the current plan now"
          />
          <RadioField
            id="save-mode-propose"
            name="save-mode"
            checked={value.mode === "PROPOSE"}
            onChange={() => onChange({ ...value, mode: "PROPOSE" })}
            label="Save as a proposal: a staff-only draft. The customer sees nothing until you activate it."
          />
        </FieldGroup>
      ) : (
        <p className={hintClass}>
          This draft is edited in place. The customer sees it only after it is activated.
        </p>
      )}
      <TextAreaField
        id="itinerary-reason"
        label="Reason (staff only)"
        value={value.reason}
        onChange={(reason) => onChange({ ...value, reason })}
        rows={2}
        maxLength={MAX_REASON}
        required={reasonRequired}
        error={reasonError}
        hint={
          reasonRequired
            ? "Needed for a change that touches a fixed item, an item with a request, or a payment. It goes into the audit history."
            : "Optional. It goes into the audit history. Never shown to the customer."
        }
      />
    </>
  );
}

/** Whether the reason box must be filled before the first send. */
export const reasonNeeded = (
  context: PlanContext,
  save: SaveState,
  touchesConsequential: boolean,
  serverAsked: boolean,
): boolean =>
  serverAsked ||
  (context.detail.itinerary.status === "ACTIVE" && save.mode === "APPLY" && touchesConsequential);

export function focusFirstInvalidInDialog(): void {
  queueMicrotask(() => {
    document.querySelector<HTMLElement>('.dialog [aria-invalid="true"]')?.focus();
  });
}
